import { useState, memo, useEffect, useRef } from 'react'
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  DragMoveEvent
} from '@dnd-kit/core'
import { restrictToSpecificContainer } from './utils'
import { MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core'

import DndItem from './DndItem'
import * as Styled from './styled'
import { IProps, IDndListItem } from './types'

const DndList = ({
  data,
  onDragEnd,
  onDragStart,
  boundingContainerId
}: IProps) => {
  const [items, setItems] = useState(data)
  const containerRef = useRef<HTMLDivElement>(null)
  const dropIndicatorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setItems(data)
  }, [data])

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 8
    }
  })

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 250,
      tolerance: 5
    }
  })

  const sensors = useSensors(mouseSensor, touchSensor)

  const calculateNewPositions = (
    draggedItem: IDndListItem,
    dragOffsetX: number
  ) => {
    const containerWidth = containerRef.current?.clientWidth || 0

    const draggedItemLeft = Math.min(
      draggedItem.left + dragOffsetX,
      containerWidth - draggedItem.width
    )

    const nonDraggedItems = items.filter(item => item.id !== draggedItem.id)

    const reorderedItems = [
      ...nonDraggedItems,
      { ...draggedItem, left: draggedItemLeft }
    ].sort((a, b) => a.left - b.left)

    const draggedIndex = reorderedItems.findIndex(
      item => item.id === draggedItem.id
    )
    const prevItemRightEdge =
      draggedIndex > 0
        ? reorderedItems[draggedIndex - 1].left +
          reorderedItems[draggedIndex - 1].width
        : 0
    const nextItemLeftEdge =
      draggedIndex < reorderedItems.length - 1
        ? reorderedItems[draggedIndex + 1].left
        : containerWidth

    const gapToPreviousItem = draggedItemLeft - prevItemRightEdge
    const overlapWithNextItem =
      draggedItemLeft + draggedItem.width - nextItemLeftEdge
    const leftShiftAmount =
      gapToPreviousItem > 0 && overlapWithNextItem > 0
        ? Math.min(overlapWithNextItem, gapToPreviousItem)
        : 0

    const finalLeftPosition = Math.max(draggedItemLeft, prevItemRightEdge)
    const availableSpace = nextItemLeftEdge - finalLeftPosition
    const rightShiftAmount =
      draggedItem.width > availableSpace
        ? draggedItem.width - availableSpace
        : 0

    return {
      reorderedItems,
      draggedIndex,
      finalLeftPosition,
      leftShiftAmount,
      rightShiftAmount,
      nonDraggedItems
    }
  }

  const onDragMove = (event: DragMoveEvent) => {
    const { delta, active } = event
    if (!containerRef.current) return

    const containerEl = containerRef.current
    const draggedItem = items.find(item => item.id === active.id)
    if (!draggedItem) return

    const {
      reorderedItems,
      draggedIndex,
      finalLeftPosition,
      leftShiftAmount,
      rightShiftAmount,
      nonDraggedItems
    } = calculateNewPositions(draggedItem, delta.x)

    nonDraggedItems.forEach(nonDraggedItem => {
      const el = containerEl.querySelector(
        `[data-id="${nonDraggedItem.id}"]`
      ) as HTMLElement

      if (!el) return

      const itemIndex = reorderedItems.findIndex(
        item => item.id === nonDraggedItem.id
      )

      if (itemIndex > draggedIndex) {
        el.style.transform = `translateX(${rightShiftAmount - leftShiftAmount}px)`
      } else {
        el.style.transform = 'translateX(0)'
      }
    })

    if (dropIndicatorRef.current) {
      dropIndicatorRef.current.style.display = 'block'
      dropIndicatorRef.current.style.left = `${finalLeftPosition - leftShiftAmount}px`
      dropIndicatorRef.current.style.width = `${draggedItem.width}px`
    }
  }

  const _onDragEnd = (event: DragEndEvent) => {
    const { delta, active } = event
    if (!containerRef.current) return

    const containerEl = containerRef.current

    // Hide drop indicator
    if (dropIndicatorRef.current) {
      dropIndicatorRef.current.style.display = 'none'
    }

    // Reset transform styles on all items
    items.forEach(item => {
      const el = containerEl.querySelector(
        `[data-id="${item.id}"]`
      ) as HTMLElement
      if (el) {
        el.style.transform = ''
      }
    })

    const containerWidth = containerEl.clientWidth

    setItems(prev => {
      const draggedItem = prev.find(item => item.id === active.id)
      if (!draggedItem) return prev

      const {
        reorderedItems,
        draggedIndex,
        finalLeftPosition,
        leftShiftAmount,
        rightShiftAmount
      } = calculateNewPositions(draggedItem, delta.x)

      const updated = reorderedItems.map((item, i) => {
        if (i < draggedIndex) {
          return item
        } else if (i === draggedIndex) {
          return {
            ...item,
            left: Math.max(
              0,
              Math.min(
                finalLeftPosition - leftShiftAmount,
                containerWidth - item.width
              )
            )
          }
        } else {
          return {
            ...item,
            left: Math.max(
              0,
              Math.min(
                item.left + rightShiftAmount - leftShiftAmount,
                containerWidth - item.width
              )
            )
          }
        }
      })

      onDragEnd(updated)
      return updated
    })
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={_onDragEnd}
      onDragStart={onDragStart}
      onDragMove={onDragMove}
      modifiers={[
        ...(boundingContainerId
          ? [restrictToSpecificContainer(boundingContainerId)]
          : [])
      ]}
    >
      <Styled.DndList ref={containerRef}>
        {items.map(item => (
          <DndItem key={item.id} data={item} />
        ))}
        <Styled.DropIndicator ref={dropIndicatorRef} />
      </Styled.DndList>
    </DndContext>
  )
}

export default memo(DndList)
